import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Renderer2, DOCUMENT } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PartnerPortalSettings } from '../interfaces/partner-portal/partner-portal-settings';

import { environment } from 'src/environments/environment';
import { b2bSettings } from "src/data/settings-loader";
import { B2bSettings } from '../interfaces/settings/local-b2b-settings';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private style: HTMLLinkElement;
  private cssFile: string;
  private themeCSSID: string = 'themeCSS';
  settings: PartnerPortalSettings;
  b2bSettings: B2bSettings;

  constructor(private http: HttpClient,  @Inject(DOCUMENT) private document: Document) {
  }

  getSettings(noCache = false): Observable<PartnerPortalSettings> {
    const storeName = localStorage.getItem("storeName") !== null ? localStorage.getItem("storeName") : environment.storeName;
    const storageKey = `partner-portal-settings-${storeName}`;
    const localStorageSettings = localStorage.getItem(storageKey);

    // Check if settings exist in localStorage and are not expired
    if (localStorageSettings && !noCache) {
      const cached = JSON.parse(localStorageSettings);
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;
      if (cached.timestamp && (now - cached.timestamp < oneDay)) {
        this.settings = cached.data;
        return of(cached.data);
      } else {
        localStorage.removeItem(storageKey); // Remove expired cache
      }
    }

    // Fetch settings from the JSON file
    return this.http.get(`/assets/store-settings-${storeName}.json`).pipe(
      map((response: PartnerPortalSettings) => {
        this.settings = response;
        // Store settings in localStorage with timestamp
        localStorage.setItem(
          storageKey,
          JSON.stringify({ data: response, timestamp: Date.now() })
        );
        return response;
      }),
      catchError((error: any) => {
        // Handle error if fetching JSON file fails
        console.error('Error fetching app settings:', error);
        return of(null); // Return an empty observable or handle the error accordingly
      })
    );
  }

  getB2bLocalSettings(companyGroupCode): B2bSettings {
    const settings = b2bSettings;
    this.b2bSettings = settings[companyGroupCode] as B2bSettings;
    return this.b2bSettings
  }

  hasChat(language: string): boolean {
    return this.b2bSettings?.general?.chat?.enabled_languages?.includes(language) ?? false;
  }

  setTheme(renderer2: Renderer2, storeName: string = environment.storeName) {
    if (localStorage.getItem("storeName") !== null) {
      storeName = localStorage.getItem("storeName");
    }
    this.cssFile = `assets/partner-portal/layout-${storeName}.css`;
    this.removeExistingThemeStyle(renderer2, this.themeCSSID);

    // Create a link element via Angular's renderer to avoid SSR troubles
    this.style = renderer2.createElement('link') as HTMLLinkElement;

    // Set type of the link item and path to the css file
    renderer2.setProperty(this.style, 'rel', 'stylesheet');
    renderer2.setProperty(this.style, 'href', this.cssFile);
    renderer2.setProperty(this.style, 'id', this.themeCSSID);

    // Add the style to the head section
    renderer2.appendChild(this.document.head, this.style);
  }

  removeExistingThemeStyle(renderer2: Renderer2, themeCSSID: string) {
    const themeIDHTMlElem = this.document.getElementById(themeCSSID);
    if (themeIDHTMlElem) {
      renderer2.removeChild(this.document.head, themeIDHTMlElem);
    }
  }
}
